import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BookMarked, Plus, Trash2, Edit, Layers, CheckSquare, Square } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';
import { stateRequirements } from '../stateRequirements';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATE_CODES = Object.keys(stateRequirements).sort();

// Sort grade labels naturally: K/Kindergarten first, then numeric grades, then anything else.
const gradeSortKey = (g) => {
  const v = String(g ?? '').trim().toLowerCase();
  if (v === 'k' || v === 'kindergarten' || v === 'pre-k' || v === 'prek') return -1;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? 999 : n;
};

const AdminStandardsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState('LA');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: '', description: '', subject: '', grade_level: '' });
  const [editing, setEditing] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);  // For bulk delete

  const adminHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadStandards = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards?state=${selectedState}`, { headers: adminHeaders() });
      if (res.status === 401) { navigate('/admin/login'); return; }
      const data = await res.json();
      setStandards(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load standards', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedState, navigate, toast]);

  useEffect(() => { loadStandards(); }, [loadStandards]);

  // Reset grade & subject filters whenever the state changes (they differ per state).
  useEffect(() => { setSelectedGrade('all'); setSelectedSubject('all'); }, [selectedState]);

  // Unique grade values present in the loaded standards, naturally sorted.
  const availableGrades = Array.from(
    new Set(standards.map(s => (s.grade_level ?? '').toString().trim()).filter(Boolean))
  ).sort((a, b) => gradeSortKey(a) - gradeSortKey(b) || a.localeCompare(b));

  // Unique subject values present in the loaded standards, alphabetically sorted.
  const availableSubjects = Array.from(
    new Set(standards.map(s => (s.subject ?? '').toString().trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Standards matching the selected grade + subject filters.
  const matchesGrade = (s) => selectedGrade === 'all'
    ? true
    : selectedGrade === '__none__'
      ? !((s.grade_level ?? '').toString().trim())
      : (s.grade_level ?? '').toString().trim() === selectedGrade;
  const matchesSubject = (s) => selectedSubject === 'all'
    ? true
    : selectedSubject === '__none__'
      ? !((s.subject ?? '').toString().trim())
      : (s.subject ?? '').toString().trim() === selectedSubject;
  const filteredStandards = standards.filter(s => matchesGrade(s) && matchesSubject(s));

  // Group the filtered standards by grade for a "separated" display.
  const groupedByGrade = filteredStandards.reduce((acc, s) => {
    const key = (s.grade_level ?? '').toString().trim() || 'Ungraded';
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});
  const gradeGroupKeys = Object.keys(groupedByGrade).sort(
    (a, b) => gradeSortKey(a === 'Ungraded' ? '' : a) - gradeSortKey(b === 'Ungraded' ? '' : b) || a.localeCompare(b)
  );

  const handleAdd = async () => {
    if (!form.code.trim() && !form.description.trim()) {
      toast({ title: 'Missing info', description: 'Enter a code or description', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards`, {
        method: 'POST',
        headers: adminHeaders(),
        body: JSON.stringify({ state: selectedState, ...form, subject: form.subject || null, grade_level: form.grade_level || null })
      });
      if (!res.ok) throw new Error('failed');
      toast({ title: 'Standard added', description: `${form.code || 'Standard'} added to ${selectedState}` });
      setForm({ code: '', description: '', subject: '', grade_level: '' });
      loadStandards();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add standard', variant: 'destructive' });
    }
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards/${editing.id}`, {
        method: 'PUT',
        headers: adminHeaders(),
        body: JSON.stringify({
          code: editing.code,
          description: editing.description,
          subject: editing.subject || null,
          grade_level: editing.grade_level || null
        })
      });
      if (!res.ok) throw new Error('failed');
      toast({ title: 'Standard updated' });
      setIsEditOpen(false);
      setEditing(null);
      loadStandards();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to update standard', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this standard? Plans/assignments tagged with it will lose the tag.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards/${id}`, { method: 'DELETE', headers: adminHeaders() });
      if (!res.ok) throw new Error('failed');
      toast({ title: 'Standard deleted' });
      loadStandards();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete standard', variant: 'destructive' });
    }
  };

  // Toggle selection of a single standard
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Select/deselect all currently filtered standards
  const toggleSelectAll = () => {
    const filteredIds = filteredStandards.map(s => s.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  // Bulk delete selected standards
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected standard(s)? Plans/assignments tagged with them will lose the tag.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards/bulk-delete`, {
        method: 'POST',
        headers: adminHeaders(),
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast({ title: 'Standards deleted', description: data.message });
      setSelectedIds([]);
      loadStandards();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete standards', variant: 'destructive' });
    }
  };

  // Clear selection when state/filters change
  useEffect(() => { setSelectedIds([]); }, [selectedState, selectedGrade, selectedSubject]);

  const handleBulk = async () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    const items = lines.map(line => {
      const parts = line.split('|').map(p => p.trim());
      return { code: parts[0] || '', description: parts[1] || '', subject: parts[2] || null, grade_level: parts[3] || null };
    }).filter(i => i.code || i.description);
    if (items.length === 0) {
      toast({ title: 'Nothing to add', description: 'Enter at least one line', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/standards/bulk`, {
        method: 'POST',
        headers: adminHeaders(),
        body: JSON.stringify({ state: selectedState, standards: items })
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast({ title: 'Standards imported', description: data.message });
      setBulkText('');
      setIsBulkOpen(false);
      loadStandards();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to import standards', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <BookMarked className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Academic Standards</h1>
              <p className="text-sm text-slate-500">Input standards per state that parents can tag on lesson plans and assignments</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-56">
              <Label className="text-xs text-slate-500 mb-1 block">State</Label>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger data-testid="admin-standards-state-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATE_CODES.map(code => (
                    <SelectItem key={code} value={code}>{code} — {stateRequirements[code].name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Grade</Label>
              <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                <SelectTrigger data-testid="admin-standards-grade-select">
                  <SelectValue placeholder="All Grades" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Grades</SelectItem>
                  {availableGrades.map(g => (
                    <SelectItem key={g} value={g}>Grade {g}</SelectItem>
                  ))}
                  <SelectItem value="__none__">Ungraded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Subject</Label>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger data-testid="admin-standards-subject-select">
                  <SelectValue placeholder="All Subjects" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Subjects</SelectItem>
                  {availableSubjects.map(sub => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ))}
                  <SelectItem value="__none__">No subject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="mt-5" onClick={() => setIsBulkOpen(true)} data-testid="admin-bulk-standards-btn">
              <Layers className="w-4 h-4 mr-2" /> Bulk Add
            </Button>
          </div>
        </div>

        {/* Add form */}
        <Card className="p-4 mb-6 border-0 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3">
              <Label className="text-xs">Code</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. LA.MATH.3.OA.1" data-testid="standard-code-input" />
            </div>
            <div className="md:col-span-5">
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What the standard covers" data-testid="standard-desc-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Subject</Label>
              <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Math" data-testid="standard-subject-input" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Grade</Label>
              <Input value={form.grade_level} onChange={e => setForm({ ...form, grade_level: e.target.value })} placeholder="3" data-testid="standard-grade-input" />
            </div>
            <div className="md:col-span-1">
              <Button onClick={handleAdd} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="add-standard-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* List */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              {filteredStandards.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={toggleSelectAll}
                  className="text-slate-600 h-8"
                  data-testid="select-all-standards-btn"
                >
                  {filteredStandards.length > 0 && filteredStandards.every(s => selectedIds.includes(s.id)) ? (
                    <CheckSquare className="w-4 h-4 mr-1.5" />
                  ) : (
                    <Square className="w-4 h-4 mr-1.5" />
                  )}
                  Select All
                </Button>
              )}
              <span className="font-semibold text-slate-700">
                {selectedState} standards{selectedGrade !== 'all' && selectedGrade !== '__none__' ? ` · Grade ${selectedGrade}` : selectedGrade === '__none__' ? ' · Ungraded' : ''}{selectedSubject !== 'all' && selectedSubject !== '__none__' ? ` · ${selectedSubject}` : selectedSubject === '__none__' ? ' · No subject' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedIds.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBulkDelete}
                  className="h-8"
                  data-testid="bulk-delete-standards-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete {selectedIds.length} Selected
                </Button>
              )}
              <Badge className="bg-blue-100 text-blue-700" data-testid="standards-count-badge">{filteredStandards.length}</Badge>
            </div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading…</div>
          ) : filteredStandards.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="no-standards-msg">
              {standards.length === 0
                ? `No standards yet for ${selectedState}. Add one above or use Bulk Add.`
                : `No standards match the selected filters for ${selectedState}.`}
            </div>
          ) : (
            <div>
              {gradeGroupKeys.map(gradeKey => (
                <div key={gradeKey} data-testid={`grade-group-${gradeKey}`}>
                  <div className="px-4 py-2 bg-slate-100 border-b flex items-center gap-2 sticky top-0">
                    <Layers className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">
                      {gradeKey === 'Ungraded' ? 'Ungraded' : `Grade ${gradeKey}`}
                    </span>
                    <Badge variant="outline" className="text-xs">{groupedByGrade[gradeKey].length}</Badge>
                  </div>
                  <div className="divide-y">
                    {groupedByGrade[gradeKey].map(s => (
                      <div key={s.id} className={`flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50 ${selectedIds.includes(s.id) ? 'bg-blue-50' : ''}`} data-testid={`standard-row-${s.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Checkbox
                            checked={selectedIds.includes(s.id)}
                            onCheckedChange={() => toggleSelect(s.id)}
                            data-testid={`select-standard-${s.id}`}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-semibold text-slate-800">{s.code}</span>
                              {s.subject && <Badge variant="outline" className="text-xs">{s.subject}</Badge>}
                              {s.grade_level && <Badge variant="outline" className="text-xs">Gr {s.grade_level}</Badge>}
                            </div>
                            <p className="text-sm text-slate-600 mt-0.5">{s.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => { setEditing(s); setIsEditOpen(true); }} data-testid={`edit-standard-btn-${s.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(s.id)} data-testid={`delete-standard-btn-${s.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Edit Standard</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Code</Label>
                <Input value={editing.code || ''} onChange={e => setEditing({ ...editing, code: e.target.value })} data-testid="edit-standard-code" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} data-testid="edit-standard-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={editing.subject || ''} onChange={e => setEditing({ ...editing, subject: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Grade</Label>
                  <Input value={editing.grade_level || ''} onChange={e => setEditing({ ...editing, grade_level: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleUpdate} data-testid="save-standard-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Bulk Add Standards — {selectedState}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">One per line. Format: <code>code | description | subject | grade</code> (subject &amp; grade optional).</p>
          <Textarea
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            rows={10}
            placeholder={"LA.MATH.3.OA.1 | Interpret products of whole numbers | Math | 3\nLA.ELA.3.RL.1 | Ask and answer questions about a text | ELA | 3"}
            data-testid="bulk-standards-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleBulk} data-testid="import-standards-btn">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStandardsPage;
